import { GROUP_TYPE, TYPE_PER_GROUP_TYPE, getCountsCategory } from "@/constants/system-constants";
import { useTabCount } from "@/hooks/useCategoryTab";
import { Filter } from "@/models/Filter";
import SystemStat from "@/models/SystemStat";
import { getSystemStats } from "@/services/systemService";
import { getListOfSiteIdsFromFilter } from "@/utilities/tabUtils";
import { RouterRootState } from "connected-react-router";
import { FC, ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import ActivityFrame from "../../../components/activity-frame/activity-frame";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import Tabs, { getTabsListFromLabels } from "../../../components/tabs/tabs";
import { ACCESS_TYPES } from "../../../constants/access-constants";
import AccessTab from "../../../containers/access-group-tab";
import { useLocale } from "../../../contexts/localeContext";
import Restricted from "../../../contexts/PermissionProvider/RestrictedWrapper";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import plusIcon from "../../../images/Plus.svg";
import { Category } from "../../../models/Category";
import { showError } from "../../../services/utilities/ServiceError";

const getRouteNameFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

interface AccessGroupsProps {
    selected: number;
    setSelected: (index: number) => void;
}

const AccessGroups: FC<AccessGroupsProps> = ({ selected, setSelected }) => {
    const [busy, setBusy] = useState(false);
    const history = useHistory();
    const { translate } = useLocale();
    const [categories, setCategories] = useState<Category[]>([
        {
            displayName: translate.byKey(TranslationKeys.accessGroups),
            heading: translate.byKey(TranslationKeys.accessGroups),
            value: "",
            id: 1,
            count: 0
        },
        {
            displayName: translate.byKey(TranslationKeys.floorGroups),
            heading: translate.byKey(TranslationKeys.floorGroups),
            value: "",
            id: 2,
            count: 0
        }
    ]);
    const [tabContent, setTabContent] = useState<ReactNode[]>([]);
    const routeName = useSelector((state: RouterRootState) => getRouteNameFromPath(state.router.location.pathname));
    const { updateTabCount } = useTabCount({
        tableName: "AccessGroup",
        categories,
        setCategories,
        customFilters: {
            agType: `=${selected === 0 ? ACCESS_TYPES.ACCESS_GROUP : ACCESS_TYPES.FLOOR_GROUP}`
        }
    });

    useEffect(() => {
        getCategoriesTotals();
    }, []);

    const getCategoriesTotals = async () => {
        try {
            setBusy(true);
            const showTabCounts = window.sessionStorage.getItem("showTabCounts") === "1";
            const systemStats: SystemStat | undefined = showTabCounts ? await getSystemStats(TYPE_PER_GROUP_TYPE) : undefined;

            const updatedCategories = categories.map(category => {
                if (category.id === 1) {
                    return { ...category, count: systemStats ? getCountsCategory(systemStats, GROUP_TYPE.ACCESS_GROUP) : 0 };
                }
                else if (category.id === 2) {
                    return { ...category, count: systemStats ? getCountsCategory(systemStats, GROUP_TYPE.FLOOR_GROUP) : 0 };
                }

                return category;
            });

            setCategories(updatedCategories);
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => {
        if (categories.length > 0) {
            const newTabContent = [
                <AccessTab
                    key={"accessGroup"}
                    setBusy={setBusy}
                    categories={categories}
                    setCategories={setCategories}
                    accesType={1} 
                    onFilterChange={onFilterChange} />,
                <AccessTab
                    key={"floorGroup"}
                    setBusy={setBusy}
                    categories={categories}
                    setCategories={setCategories}
                    accesType={2}
                    onFilterChange={onFilterChange} />
            ];
            setTabContent(newTabContent);
        }
    }, [categories]);

    const onFilterChange = (filters: Filter[]) => {
        updateTabCount(getListOfSiteIdsFromFilter(filters));
    }

    /*
      Describes the route when clicking the Add button for each tab.
    */
    const getItempPath = (selected: number) => {
        switch (selected) {
            case 0:
                return "/access-control/enrolment/access-group/";
            case 1:
                return "/access-control/enrolment/floor-group/";
            default:
                break;
        }
    }

    return <ActivityFrame
        busy={busy}
        className="flex flex-grow overflow-y-hidden">
        <div className="pt-4 pl-4 pb-4 flex flex-col flex-grow">
            <Breadcrumb
                path={[
                    {
                        value: "/access-control/enrolment",
                        displayName: translate.byKey(TranslationKeys.enrolments),
                    },
                    {
                        value: "/access-control/enrolment/access-groups",
                        displayName: categories[selected]?.heading,
                    },
                ]}
                onClick={(item) => history.push(item.value)} />
            <Tabs
                className="overflow-y-hidden"
                tabs={getTabsListFromLabels(categories)}
                onSelect={(i) => setSelected(i)}
                selected={selected}
                onAdd={() => { }}
                add={false}
                tabStyle={{}}>
                {tabContent[selected]}
                <div className="flex flex-col flex-grow overflow-y-hidden mb-viewport">
                    <div className="flex flex-row justify-end button-cluster">
                        <Restricted to={`add.${routeName}.element`}>
                            <Link to={{ pathname: getItempPath(selected) }} >
                                <button className="flex rounded-lg bg-lime text-white p-2 shadow-lg" id="addBtn">
                                    <img
                                        src={plusIcon}
                                        className="px-2 w-8 py-1"
                                        alt="" />
                                    <span className="mr-2">{translate.byKey(TranslationKeys.add)}</span>
                                </button>
                            </Link>
                        </Restricted>
                    </div>
                </div>
            </Tabs>
        </div>
    </ActivityFrame>
};

export default AccessGroups;
